// MetadadosView — CRUD do catálogo de turma_metadata_fields

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { AlertCircle, Layers, Loader2, Plus, Search, Trash2, X } from "lucide-react";
import { api, ApiError, type MetadataField } from "../api";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const fields = signal<MetadataField[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const err = signal<string | null>(null);
const creating = signal<boolean>(false);
const search = signal<string>("");

async function load() {
  phase.value = "loading";
  err.value = null;
  try {
    const r = await api.listMetadataFields();
    fields.value = r.fields;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    err.value = friendlyError(e);
    phase.value = "error";
  }
}

export function MetadadosView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading")
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {err.value}
      </div>
    );
  const rows = fields.value ?? [];
  const q = search.value.toLowerCase().trim();
  const visible = q
    ? rows.filter((f) => `${f.key} ${f.label}`.toLowerCase().includes(q))
    : rows;

  async function rm(id: string) {
    if (!confirm("Excluir este campo?")) return;
    try {
      await api.deleteMetadataField(id);
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  return (
    <div class="space-y-4">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Layers className="size-6 text-primary" />
            Metadados de turma
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Catálogo de campos extras que podem ser preenchidos nas turmas (ex: Período,
            Ano de ingresso).
          </p>
        </div>
        <button
          type="button"
          onClick={() => (creating.value = true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" /> Novo campo
        </button>
      </header>
      <div class="relative flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar key, label…"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>
      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum campo cadastrado.
        </div>
      ) : (
        <ul class="space-y-1">
          {visible.map((f) => (
            <li
              key={f.id}
              class="flex items-center gap-3 rounded-md border border-border bg-card/40 px-3 py-2 text-sm"
            >
              <Layers width="0.9em" height="0.9em" className="text-violet-600 dark:text-violet-300" />
              <div class="min-w-0 flex-1">
                <div class="font-semibold">{f.label}</div>
                <div class="font-mono text-[11px] text-muted-foreground">
                  {f.key} · {f.type}
                  {f.options && f.options.length > 0 && ` (${f.options.length} opções)`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => rm(f.id)}
                title="Excluir"
                aria-label="Excluir"
                class="rounded-md border border-red-500/40 bg-red-500/5 px-2 py-1 text-xs text-red-600 hover:bg-red-500/10 dark:text-red-400"
              >
                <Trash2 width="0.9em" height="0.9em" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {creating.value && <CreateModal />}
    </div>
  );
}

function CreateModal() {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [type, setType] = useState<"text" | "number" | "date" | "select">("text");
  const [optionsRaw, setOptionsRaw] = useState("");
  const [busy, setBusy] = useState(false);

  async function go() {
    if (!key.trim() || !label.trim()) return;
    setBusy(true);
    try {
      const opts =
        type === "select"
          ? optionsRaw
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : null;
      await api.createMetadataField({
        key: key.trim(),
        label: label.trim(),
        type,
        options: opts
      });
      creating.value = false;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={() => (creating.value = false)}
    >
      <div
        class="w-full max-w-md space-y-3 rounded-md border border-border bg-card p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="flex items-center justify-between">
          <h2 class="text-base font-bold">Novo campo de metadados</h2>
          <button
            type="button"
            onClick={() => (creating.value = false)}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </div>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Key (id)</span>
          <input
            type="text"
            value={key}
            onInput={(e) => setKey((e.target as HTMLInputElement).value.toLowerCase().replace(/\s+/g, "_"))}
            placeholder="periodo"
            aria-label="Key"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm outline-none focus:border-primary"
          />
        </label>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Label visível</span>
          <input
            type="text"
            value={label}
            onInput={(e) => setLabel((e.target as HTMLInputElement).value)}
            placeholder="Período"
            aria-label="Label"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </label>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tipo</span>
          <select
            value={type}
            onChange={(e) => setType((e.target as HTMLSelectElement).value as any)}
            aria-label="Tipo"
            class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"
          >
            <option value="text">Texto</option>
            <option value="number">Número</option>
            <option value="date">Data</option>
            <option value="select">Seleção (lista)</option>
          </select>
        </label>
        {type === "select" && (
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Opções (separadas por vírgula)</span>
            <input
              type="text"
              value={optionsRaw}
              onInput={(e) => setOptionsRaw((e.target as HTMLInputElement).value)}
              placeholder="Manhã, Tarde, Noite"
              aria-label="Opções"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </label>
        )}
        <div class="flex items-center justify-end gap-2 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => (creating.value = false)}
            disabled={busy}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={go}
            disabled={busy || !key.trim() || !label.trim()}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus width="1em" height="1em" />}
            Criar
          </button>
        </div>
      </div>
    </div>
  );
}
